/**
 * SubPageShell
 *
 * Standard layout for a top-level section that groups several existing
 * pages/components under one set of sub-tabs (Audio, Miscellaneous, Settings).
 *
 * Renders the shared <SubTabBar> at the top followed by the active sub-tab's
 * body. Children bring their own inner container/padding (each grouped page
 * keeps its existing layout), so this shell stays intentionally thin — its job
 * is only to standardise the sub-tab bar position and keep every section
 * consistent on desktop and mobile.
 */

import type { ReactNode } from 'react';
import { SubTabBar, type SubTabItem } from './SubTabBar';

interface SubPageShellProps {
  tabs: SubTabItem[];
  activeId: string;
  onChange: (id: string) => void;
  children: ReactNode;
  accent?: 'purple' | 'blue';
  ariaLabel?: string;
}

export function SubPageShell({
  tabs,
  activeId,
  onChange,
  children,
  accent = 'purple',
  ariaLabel,
}: SubPageShellProps) {
  return (
    <div className="flex flex-col flex-1 min-w-0">
      <SubTabBar
        tabs={tabs}
        activeId={activeId}
        onChange={onChange}
        accent={accent}
        ariaLabel={ariaLabel}
      />
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
